<template>
  <div class="roster-sync-modal" role="dialog" aria-modal="true" aria-labelledby="poll-form-title">
    <div class="roster-sync-modal__backdrop" @click="onBackdropClick"></div>

    <div class="roster-sync-modal__shell">
      <header class="roster-sync-modal__header">
        <div>
          <h2 id="poll-form-title" class="roster-sync-modal__title">Create a poll</h2>
          <p class="roster-sync-modal__subtitle">
            Ask a question with up to 10 options. Members will be notified just like any other message.
          </p>
        </div>
        <button type="button"
                class="harbor-chat__icon-btn"
                :disabled="submitting"
                aria-label="Close"
                @click="cancel">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="roster-sync-modal__body">
        <div class="form-group">
          <label for="poll-form-question" class="roster-sync-modal__label">Question</label>
          <input id="poll-form-question"
                 ref="questionInput"
                 v-model="question"
                 type="text"
                 class="form-control"
                 data-cy="poll-question"
                 placeholder="Ask a question..."
                 maxlength="255" />
        </div>

        <div class="form-group">
          <label class="roster-sync-modal__label">Options</label>
          <div class="poll-form__options">
            <div v-for="(option, index) in options"
                 :key="index"
                 class="poll-form__option">
              <input v-model="options[index]"
                     type="text"
                     class="form-control"
                     data-cy="poll-option-input"
                     :placeholder="'Option ' + (index + 1)"
                     maxlength="100" />
              <button v-if="options.length > 2"
                      type="button"
                      class="harbor-chat__icon-btn"
                      data-cy="poll-remove-option"
                      aria-label="Remove option"
                      @click="removeOption(index)">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <button v-if="options.length < 10"
                  type="button"
                  class="btn btn-link btn-compact poll-form__add-option"
                  data-cy="poll-add-option"
                  @click="addOption">
            + Add option
          </button>
        </div>

        <div class="form-group m-b-none">
          <label class="poll-form__toggle">
            <input type="checkbox" v-model="allowMultiple" data-cy="poll-allow-multiple" />
            <span>Allow multiple selections</span>
          </label>
        </div>

        <p v-if="error" class="text-danger m-t-sm m-b-none">{{ errorMessage }}</p>
      </div>

      <footer class="roster-sync-modal__footer">
        <button type="button"
                class="btn btn-default"
                :disabled="submitting"
                @click="cancel">
          Cancel
        </button>
        <button type="button"
                class="btn btn-primary"
                data-cy="poll-submit"
                :disabled="!isValid || submitting"
                @click="submit">
          <spinner v-if="submitting" :inline="true"></spinner>
          {{ submitting ? 'Creating...' : 'Send poll' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';

export default {
  name: 'poll-form',
  components: { Spinner },
  props: {
    groupId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      question: '',
      options: ['', ''],
      allowMultiple: false,
      submitting: false,
      error: false,
      errorMessage: '',
    };
  },
  computed: {
    isValid() {
      const hasQuestion = this.question.trim().length > 0;
      const filledOptions = this.options.filter((o) => o.trim().length > 0);
      return hasQuestion && filledOptions.length >= 2;
    },
  },
  mounted() {
    document.body.classList.add('composer-overlay-open');
    document.addEventListener('keydown', this.handleKeydown);
    this.$nextTick(() => {
      if (this.$refs.questionInput) this.$refs.questionInput.focus();
    });
  },
  destroyed() {
    document.body.classList.remove('composer-overlay-open');
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleKeydown(e) {
      if (this.submitting) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.cancel();
      }
    },
    onBackdropClick() {
      if (this.submitting) return;
      this.cancel();
    },
    addOption() {
      if (this.options.length < 10) {
        this.options.push('');
      }
    },
    removeOption(index) {
      if (this.options.length > 2) {
        this.options.splice(index, 1);
      }
    },
    submit() {
      const vm = this;
      if (!vm.isValid || vm.submitting) return;

      vm.submitting = true;
      vm.error = false;

      const filledOptions = vm.options
        .filter((o) => o.trim().length > 0)
        .map((text, index) => ({ text: text.trim(), position: index }));

      const params = {
        message: {
          content: '',
          poll_attributes: {
            question: vm.question.trim(),
            allow_multiple: vm.allowMultiple,
            poll_options_attributes: filledOptions,
          },
        },
      };

      const url = vm.$apiService.messagesUrl(vm.groupId);
      axios.post(url, params)
        .then(() => {
          vm.submitting = false;
          vm.$emit('poll-created');
          vm.$notificationManager.emit('show-toast', 'Poll created', true);
        })
        .catch((error) => {
          vm.submitting = false;
          vm.error = true;
          if (error.response && error.response.data && error.response.data.errors) {
            vm.errorMessage = error.response.data.errors.join(', ');
          } else {
            vm.errorMessage = 'Failed to create poll';
          }
        });
    },
    cancel() {
      if (this.submitting) return;
      this.$emit('poll-cancelled');
    },
  },
};
</script>
